import { unitStrict } from '@lib/style/unit';
import { FlexDirection, getJustifyAlign, JustifyAlignValue } from '@lib/style/utils';
import { classNames } from '@lib/utils';
import Grid, { GridProps, GridSpacing } from '@material-ui/core/Grid';
import { createMemo } from '@packages/react';
import { applyRef } from '@packages/react/applyRef';
import React, { forwardRef, ReactElement } from 'react';

export type RowProps<C extends React.ElementType = any> = Omit<
    GridProps<
        C,
        {
            component?: any;
            ref?: any;
            padding?: number | string;
            minWidth?: number | string;
            className?: string;
            fluid?: boolean;
            spacing?: GridSpacing;
            align?: JustifyAlignValue;
            maxWidth?: string | number;
            direction?: FlexDirection;
            layout?: FlexDirection | string;
        }
    >,
    'justify'
> & {
    justify?: JustifyAlignValue;
};

/**
 * @author: Dumitrana Alinus <alin.designstudio@gmail.com>
 * @class: Row
 * @date:  18.09.2018
 * @time:  00:53
 * @revisions:
 *  - Dumitrana Alinus [08.08.2019 14:03] -> Better Support for SSR & IDE syntax
 *  - Dumitrana Alinus [01.11.2019 00:50] -> TS support
 */
export const Row = createMemo<RowProps>(
    forwardRef<any, RowProps>((props$, ref): ReactElement => {
        const { align, layout, maxWidth, justify, padding, fluid, ...rest } = props$;

        const props: RowProps = rest;

        const align$ = getJustifyAlign(align || props.alignContent || props.alignItems) as any;

        if (justify) {
            props.justify = getJustifyAlign(justify) as any;
        }

        props.direction = props.direction || (layout as any);
        props.alignContent = align$;
        props.alignItems = align$;
        props.spacing = props.spacing || 0;
        props.className = classNames(props.className, 'ui--row');

        props.style = props.style || {};

        if (typeof padding !== 'undefined' && !props.style.hasOwnProperty('padding')) {
            props.style.padding = unitStrict(padding);
        }

        if (typeof maxWidth !== 'undefined') {
            props.style.maxWidth = unitStrict(typeof maxWidth !== 'undefined' ? maxWidth : '100%');
        }

        if (fluid) {
            props.style.width = unitStrict(props.style.width ?? '100%');
        }

        applyRef(props, ref);

        return <Grid container {...(props as any)} />;
    }),
    {
        displayName: 'Row',
        defaultProps: {
            layout: 'row',
        },
    },
);
